<template>
    <div class="dbhome-page">
        <!-- 未登录提示 -->
        <div v-if="!isLogin" class="dbhome-notice no-select">
            <el-icon class="dbhome-notice__icon">
                <Coin />
            </el-icon>
            <span>登录后可执行共享的数据库 SQL</span>
        </div>

        <div class="dbhome-layout">
            <!-- 右侧：连接页签（每个连接一个独立的 SQL 控制台） -->
            <section class="dbhome-main">
                <!-- 页签栏右侧入口：只作「连接」开关，不重复显示当前库名（库名已在页签上） -->
                <div class="dbhome-sidewrap no-select" @mouseenter="onSideEnter" @mouseleave="onSideLeave">
                    <button type="button" class="dbhome-crumb" :class="{ 'is-open': sideOpen }"
                        :title="sideOpen ? '收起连接列表' : '展开连接列表'" @click="toggleSide">
                        <el-icon class="dbhome-crumb__icon">
                            <Fold v-if="sideOpen" />
                            <Expand v-else />
                        </el-icon>
                        <span class="dbhome-crumb__label">连接</span>
                        <span class="dbhome-crumb__count">{{ filteredList.length }}</span>
                    </button>
                    <aside class="dbhome-side" :class="{ 'is-open': sideOpen }">
                        <div class="dbhome-side__toolbar">
                            <el-select v-model="selectedTeamId" class="dbhome-team" placeholder="全部团队" clearable
                                v-if="isLogin" @visible-change="onSideDropdownVisible">
                                <el-option v-for="team in visibleTeamList" :key="team.id"
                                    :label="`${team.name} 【${teamMatchCounts[team.id] || 0}】`" :value="team.id" />
                            </el-select>
                            <el-input v-model="searchKeyword" class="dbhome-search" placeholder="搜索连接名称或地址" clearable>
                                <template #prefix>
                                    <el-icon>
                                        <Search />
                                    </el-icon>
                                </template>
                            </el-input>
                        </div>

                        <div class="dbhome-side__list">
                            <div class="dbhome-group" v-for="group in connGroupList" :key="group.key">
                                <div class="dbhome-group__head" v-if="showGroupHeader">
                                    <span class="dbhome-group__name">{{ group.name }}</span>
                                    <span class="dbhome-group__count">{{ group.list.length }} 个</span>
                                </div>
                                <ul class="dbhome-conns">
                                    <li v-for="conn in group.list" :key="conn.id" class="dbhome-conn"
                                        :class="{ 'is-active': activeTab === conn.id }" @click="openConn(conn)">
                                        <span class="dbhome-conn__name" :title="conn.name">{{ conn.name }}</span>
                                        <!-- i 标志：悬浮显示描述 + 只读团队 + 读写团队 -->
                                        <el-tooltip placement="right" effect="light" :show-after="120">
                                            <template #content>
                                                <div class="dbhome-tip">
                                                    <p class="dbhome-tip__desc">{{ conn.remark || '暂无描述' }}</p>
                                                    <p>只读团队：{{ teamNames(conn.teamList) }}</p>
                                                    <p>读写团队：{{ teamNames(conn.rwTeamList) }}</p>
                                                </div>
                                            </template>
                                            <el-icon class="dbhome-conn__info" @click.stop>
                                                <InfoFilled />
                                            </el-icon>
                                        </el-tooltip>
                                    </li>
                                </ul>
                            </div>

                            <div class="dbhome-side__empty" v-if="listLoaded && filteredList.length === 0">
                                <el-icon :size="22">
                                    <Search />
                                </el-icon>
                                <p>{{ searchKeyword || selectedTeamId ? '没有匹配的连接' : (isLogin ? '暂无共享的数据库连接' : '登录后查看共享连接') }}</p>
                            </div>
                        </div>
                    </aside>
                </div>
                <el-tabs v-if="openTabs.length" v-model="activeTab" class="dbhome-tabs" closable
                    @tab-remove="closeTab">
                    <el-tab-pane v-for="c in openTabs" :key="c.id" :name="c.id" lazy>
                        <template #label>
                            <span class="dbhome-conn-tab">
                                <el-icon class="dbhome-conn-tab__icon">
                                    <Coin />
                                </el-icon>
                                <span class="dbhome-conn-tab__name">{{ c.name }}</span>
                            </span>
                        </template>
                        <db-console :conn="c" @changed="getShareList" />
                    </el-tab-pane>
                </el-tabs>
                <div v-else class="dbhome-main__empty">
                    <el-icon :size="26">
                        <Monitor />
                    </el-icon>
                    <p>点击右上角「连接」选择一个连接</p>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import { ElMessageBox } from 'element-plus';
import request from '../../utils/request.js';
import DbConsole from './components/DbConsole.vue';

const UNGROUPED_KEY = '__ungrouped__';
// 浏览器缓存：打开的连接页签（刷新后恢复）
const TABS_STORAGE_KEY = 'hidoc_dbman_open_tabs';
// 浏览器缓存：每个连接下打开的 SQL 块与 SQL 文本（由 DbConsole 读写，关闭页签时清理）
const BLOCKS_STORAGE_PREFIX = 'hidoc_dbman_blocks_';

export default {
    name: 'DataBaseMan',
    components: { DbConsole },
    data() {
        return {
            isLogin: false,
            searchKeyword: '',
            selectedTeamId: '',
            teamStorageKey: 'hidoc_dbman_selected_team_id',
            teamList: [],
            connList: [],
            listLoaded: false,
            // 已打开的连接（每个 = 一个页签 + 一个独立的控制台实例）
            openTabs: [],
            // 当前激活的页签（值为连接 id）
            activeTab: '',
            // 是否已从浏览器缓存恢复过页签（只恢复一次，后续刷新列表不覆盖用户操作）
            tabsRestored: false,
            // 左侧连接列表浮层是否展开（默认收起，只留面包屑按钮）
            sideOpen: false,
            // 鼠标是否还停在面包屑按钮/浮层上（浮层内操作时不要收起）
            sideHover: false,
            // 浮层里的团队筛选下拉是否展开（它的下拉 teleport 到 body，鼠标移进去会被判为「离开」）
            sideDropdownOpen: false,
            // 浮层自动收起的定时器（鼠标移开后延迟关闭，留出「从按钮移到浮层」的时间）
            sideCloseTimer: null
        };
    },
    watch: {
        selectedTeamId(val) {
            localStorage.setItem(this.teamStorageKey, val || '');
        },
        // 激活页签变化 → 记住
        activeTab() {
            this.persistTabs();
        }
    },
    computed: {
        keywordFilteredList() {
            const list = this.connList;
            if (!this.searchKeyword) return list;
            const keywords = this.searchKeyword.trim().split(/\s+/).filter(k => k.length > 0);
            if (keywords.length === 0) return list;
            return list.filter(item => keywords.every(keyword => {
                const kw = keyword.toLowerCase();
                // 只搜连接本身（SQL 块明细在打开控制台时按需分页拉取，不在列表里）
                return (item.name || '').toLowerCase().includes(kw)
                    || (item.host || '').toLowerCase().includes(kw);
            }));
        },
        filteredList() {
            if (!this.selectedTeamId) return this.keywordFilteredList;
            return this.keywordFilteredList.filter(item =>
                (item.teamList || []).some(t => t.id === this.selectedTeamId)
                || (item.rwTeamList || []).some(t => t.id === this.selectedTeamId));
        },
        teamMatchCounts() {
            const counts = {};
            this.teamList.forEach(team => {
                counts[team.id] = 0;
            });
            this.keywordFilteredList.forEach(item => {
                const ids = [];
                (item.teamList || []).forEach(t => ids.push(t.id));
                (item.rwTeamList || []).forEach(t => ids.push(t.id));
                ids.forEach(id => {
                    if (counts[id] !== undefined) counts[id]++;
                });
            });
            return counts;
        },
        visibleTeamList() {
            return this.teamList.filter(team =>
                team.id === this.selectedTeamId || (this.teamMatchCounts[team.id] || 0) > 0
            );
        },
        connGroupList() {
            const groups = new Map();
            this.filteredList.forEach(conn => {
                const name = (conn.groupName || '').trim();
                const key = name || UNGROUPED_KEY;
                if (!groups.has(key)) {
                    groups.set(key, { key, name: name || '未分组', list: [] });
                }
                groups.get(key).list.push(conn);
            });
            const named = [];
            let ungrouped = null;
            groups.forEach(group => {
                if (group.key === UNGROUPED_KEY) ungrouped = group;
                else named.push(group);
            });
            named.sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
            return ungrouped ? named.concat(ungrouped) : named;
        },
        showGroupHeader() {
            return this.connGroupList.some(group => group.key !== UNGROUPED_KEY);
        }
    },
    mounted() {
        document.title = 'Hidoc-数据库管理';
        this.isLogin = !!(this.$store.state.user.token);
        if (this.isLogin) {
            this.getTeamFilterOptions();
        }
        this.getShareList();
    },
    beforeUnmount() {
        this.cancelSideClose();
    },
    methods: {
        getShareList() {
            return request({
                url: '/dbMan/shareList',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.connList = res.data || [];
                    this.restoreTabs();
                    this.syncOpenTabs();
                }
                this.listLoaded = true;
            });
        },
        // 从浏览器缓存恢复「打开的连接页签」（只在连接列表首次加载后执行一次）
        restoreTabs() {
            if (this.tabsRestored) return;
            this.tabsRestored = true;
            let saved = null;
            try {
                saved = JSON.parse(localStorage.getItem(TABS_STORAGE_KEY) || 'null');
            } catch (e) {
                saved = null;
            }
            if (!saved || !Array.isArray(saved.ids) || !saved.ids.length) return;
            const list = saved.ids
                .map(id => this.connList.find(x => x.id === id))
                .filter(Boolean);
            if (!list.length) {
                this.persistTabs();
                return;
            }
            this.openTabs = list;
            this.activeTab = list.some(c => c.id === saved.active) ? saved.active : list[0].id;
            this.persistTabs();
        },
        // 记住当前打开的页签（按连接 id，连接对象以最新列表为准）
        persistTabs() {
            try {
                localStorage.setItem(TABS_STORAGE_KEY, JSON.stringify({
                    ids: this.openTabs.map(c => c.id),
                    active: this.activeTab
                }));
            } catch (e) {
                // 浏览器存储不可用/已满时忽略，不影响主流程
            }
        },
        // 连接列表刷新后，同步已打开页签里的连接对象（保证控制台拿到最新数据）
        syncOpenTabs() {
            if (!this.openTabs.length) return;
            this.openTabs = this.openTabs.map(c => this.connList.find(x => x.id === c.id) || c);
        },
        getTeamFilterOptions() {
            return request({
                url: '/team/getMyJoinTeams',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0 && res.data) {
                    this.teamList = res.data.map(t => ({ id: t.id, name: t.name }))
                        .sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
                    const cached = localStorage.getItem(this.teamStorageKey);
                    if (cached && this.teamList.some(t => t.id === cached)) {
                        this.selectedTeamId = cached;
                    }
                }
            });
        },
        // 面包屑按钮：点击展开/收起连接列表浮层
        toggleSide() {
            this.cancelSideClose();
            this.sideOpen = !this.sideOpen;
            // 收起时一并清掉下拉标记，避免下次展开后「永不收起」
            if (!this.sideOpen) this.sideDropdownOpen = false;
        },
        // 鼠标进入按钮/浮层 → 取消待执行的自动收起
        onSideEnter() {
            this.sideHover = true;
            this.cancelSideClose();
        },
        // 鼠标离开 → 延迟收起（留出从按钮移到浮层、跨过中间间隙的时间）
        onSideLeave() {
            this.sideHover = false;
            this.scheduleSideClose();
        },
        cancelSideClose() {
            if (this.sideCloseTimer) {
                clearTimeout(this.sideCloseTimer);
                this.sideCloseTimer = null;
            }
        },
        scheduleSideClose() {
            this.cancelSideClose();
            this.sideCloseTimer = setTimeout(() => {
                this.sideCloseTimer = null;
                // 鼠标又回到浮层上、或团队下拉还开着 → 先不收起
                if (this.sideHover || this.sideDropdownOpen) return;
                this.sideOpen = false;
            }, 260);
        },
        // 团队筛选下拉开/合：展开期间保持浮层，关闭后再按鼠标位置决定是否收起
        onSideDropdownVisible(visible) {
            this.sideDropdownOpen = visible;
            if (visible) this.cancelSideClose();
            else this.scheduleSideClose();
        },
        // 点左侧连接：打开或激活对应页签
        openConn(conn) {
            if (!this.isLogin) return;
            if (!this.openTabs.some(c => c.id === conn.id)) {
                this.openTabs.push(conn);
            }
            this.activeTab = conn.id;
            this.persistTabs();
        },
        // 关闭页签：先确认（未保存的 SQL 会丢失），取消则保留
        closeTab(id) {
            ElMessageBox.confirm('关闭后，该连接未保存的 SQL 语句将会丢失，确定关闭吗？', '关闭确认', {
                confirmButtonText: '确定关闭',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                const idx = this.openTabs.findIndex(c => c.id === id);
                if (idx === -1) return;
                this.openTabs.splice(idx, 1);
                if (this.activeTab === id) {
                    const next = this.openTabs[idx] || this.openTabs[idx - 1] || null;
                    this.activeTab = next ? next.id : '';
                }
                this.persistTabs();
                // 该连接页签已关闭 → 清理它的 SQL 块缓存（等子组件卸载/落盘完成后再删）
                this.$nextTick(() => {
                    try {
                        localStorage.removeItem(BLOCKS_STORAGE_PREFIX + id);
                    } catch (e) {
                        // 忽略
                    }
                });
            }).catch(() => { });
        },
        // 团队列表 → 展示文案
        teamNames(list) {
            const names = (list || []).map(t => t.name).filter(Boolean);
            return names.length ? names.join('、') : '无';
        }
    }
};
</script>

<style scoped>
.dbhome-page {
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --dbhome-text: #1f2d3d;
    --dbhome-text-sub: #5b6a80;
    --dbhome-text-weak: #8a94a6;
    --dbhome-line: #e6eaf0;

    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    color: var(--dbhome-text);
}

.dbhome-notice {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 10px 14px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 10px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary-dark-2);
    font-size: 13px;
}

.dbhome-notice__icon {
    flex: 0 0 auto;
    font-size: 15px;
}

/* 左右两栏 */
.dbhome-layout {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    gap: 14px;
}

/* ---------- 连接列表：收起为页签栏右侧入口，展开为浮层，不挤占页签左侧 ---------- */
.dbhome-sidewrap {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 45;
    display: flex;
    align-items: center;
    height: 36px;
}

.dbhome-crumb {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid var(--el-color-primary-light-5);
    border-radius: 9px;
    background-color: #fff;
    color: var(--el-color-primary);
    font-family: inherit;
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.15s, border-color 0.15s, color 0.15s;
}

.dbhome-crumb:hover,
.dbhome-crumb.is-open {
    border-color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

.dbhome-crumb:active {
    background-color: var(--el-color-primary-light-8);
}

.dbhome-crumb__icon {
    flex: 0 0 auto;
    font-size: 14px;
}

.dbhome-crumb__label {
    flex: 0 0 auto;
    font-weight: 600;
}

.dbhome-crumb__count {
    flex: 0 0 auto;
    min-width: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background-color: var(--el-color-primary-light-8);
    color: var(--el-color-primary);
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
}

.dbhome-crumb.is-open .dbhome-crumb__count {
    background-color: var(--el-color-primary);
    color: #fff;
}

.dbhome-side {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    left: auto;
    z-index: 40;
    display: none;
    flex-direction: column;
    width: min(280px, calc(100vw - 48px));
    max-height: min(560px, 68vh);
    min-height: 0;
    border: 1px solid var(--dbhome-line);
    border-radius: 12px;
    background-color: #ffffff;
    box-shadow: 0 18px 44px -20px rgba(15, 35, 70, 0.45);
    overflow: hidden;
}

.dbhome-side.is-open {
    display: flex;
}

.dbhome-side__toolbar {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border-bottom: 1px solid #eef1f6;
}

.dbhome-team,
.dbhome-search {
    width: 100%;
}

.dbhome-side__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 8px;
}

.dbhome-group {
    margin-bottom: 10px;
}

/* 分组分栏（滚动时悬浮）——样式参照 ssh/index.vue 的 .ssh-group__head */
.dbhome-group__head {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid var(--el-color-primary-light-7);
    border-left: 3px solid var(--el-color-primary);
    border-radius: 8px;
    background: linear-gradient(90deg, var(--el-color-primary-light-8) 0%, var(--el-color-primary-light-9) 100%);
    box-shadow: 0 8px 16px -14px rgba(47, 111, 237, 0.9);
}

.dbhome-group__name {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: var(--el-color-primary-dark-2);
}

.dbhome-group__count {
    padding: 2px 8px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 999px;
    background-color: #fff;
    color: var(--el-color-primary);
    font-size: 12px;
    font-weight: 500;
}

.dbhome-conns {
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
}

.dbhome-conn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 8px;
    border-radius: 8px;
    color: var(--dbhome-text);
    font-size: 13px;
    cursor: pointer;
    transition: background-color 0.15s, color 0.15s;
}

.dbhome-conn:hover {
    background-color: #f2f6fd;
}

.dbhome-conn.is-active {
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary-dark-2);
    font-weight: 600;
}

.dbhome-conn__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dbhome-conn__info {
    flex: 0 0 auto;
    color: #b3bcc9;
}

.dbhome-conn__info:hover {
    color: var(--el-color-primary);
}

.dbhome-tip {
    max-width: 260px;
}

.dbhome-tip p {
    margin: 2px 0;
    font-size: 12px;
    line-height: 1.5;
}

.dbhome-tip__desc {
    color: #1f2d3d;
    font-weight: 600;
}

.dbhome-side__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px 12px;
    color: #909399;
    font-size: 13px;
    text-align: center;
}

.dbhome-side__empty p {
    margin: 0;
}

/* ---------- 右栏：连接页签 ---------- */
.dbhome-main {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
}

.dbhome-tabs {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
}

/* 一层：连接切换。药丸选中，和顶栏导航同一套语言，比查询页签更重 */
.dbhome-tabs > :deep(.el-tabs__header) {
    flex: 0 0 auto;
    margin: 0 0 10px;
    padding: 0 96px 0 0;
    border: none;
    background: transparent;
}

.dbhome-tabs > :deep(.el-tabs__header) .el-tabs__nav-wrap::after,
.dbhome-tabs > :deep(.el-tabs__header) .el-tabs__active-bar {
    display: none;
}

.dbhome-tabs > :deep(.el-tabs__header) .el-tabs__item {
    height: 36px;
    margin: 0 6px 0 0;
    padding: 0 14px;
    border: none;
    border-radius: 9px;
    color: var(--dbhome-text-sub);
    font-size: 13px;
    font-weight: 600;
    line-height: 36px;
    transition: background-color 0.2s, color 0.2s;
}

.dbhome-tabs > :deep(.el-tabs__header) .el-tabs__item:hover {
    color: var(--dbhome-text);
    background-color: #eef2f8;
}

.dbhome-tabs > :deep(.el-tabs__header) .el-tabs__item.is-active {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
    box-shadow: inset 0 -2px 0 var(--el-color-primary);
}

.dbhome-tabs > :deep(.el-tabs__header) .el-tabs__item .is-icon-close {
    width: 16px;
    margin-left: 6px;
    color: var(--dbhome-text-weak);
}

.dbhome-tabs > :deep(.el-tabs__header) .el-tabs__item.is-active .is-icon-close,
.dbhome-tabs > :deep(.el-tabs__header) .el-tabs__item .is-icon-close:hover {
    color: var(--el-color-primary);
}

.dbhome-conn-tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 180px;
    vertical-align: middle;
}

.dbhome-conn-tab__icon {
    flex: 0 0 auto;
    font-size: 14px;
}

.dbhome-conn-tab__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dbhome-tabs > :deep(.el-tabs__content) {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    border: 1px solid var(--dbhome-line);
    border-radius: 12px;
    background-color: #ffffff;
    box-shadow: 0 10px 28px -22px rgba(31, 45, 61, 0.45);
}

.dbhome-tabs > :deep(.el-tabs__content) > .el-tab-pane {
    height: 100%;
}

.dbhome-main__empty {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 1px dashed #dcdfe6;
    border-radius: 12px;
    background-color: #ffffff;
    color: #909399;
    font-size: 13px;
}

.dbhome-main__empty p {
    margin: 0;
}

@media (max-width: 768px) {
    .dbhome-sidewrap {
        position: relative;
        top: auto;
        right: auto;
        height: auto;
        justify-content: flex-end;
        margin-bottom: 8px;
    }

    .dbhome-tabs > :deep(.el-tabs__header) {
        padding-right: 0;
    }
}
</style>
